import { cssFontFamily, DEFAULT_FONT } from '../config/fonts';
import { AdElement, ImageElement, ShapeElement, ShapeKind, TextElement } from './types';

export const createId = (): string => (
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `el-${Date.now()}-${Math.floor(Math.random() * 100000)}`
);

const base = (name: string) => ({
  id: createId(),
  name,
  rotation: 0,
  opacity: 1,
  locked: false,
  hidden: false,
});

interface Placement {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Domyślne miejsce dla nowego elementu: środek planszy, rozmiar proporcjonalny do formatu. */
export const centerPlacement = (
  sceneWidth: number,
  sceneHeight: number,
  widthRatio: number,
  heightRatio: number,
): Placement => {
  const width = Math.round(sceneWidth * widthRatio);
  const height = Math.round(sceneHeight * heightRatio);
  return {
    x: Math.round((sceneWidth - width) / 2),
    y: Math.round((sceneHeight - height) / 2),
    width,
    height,
  };
};

export type TextPreset = 'headline' | 'subtitle' | 'body' | 'badge';

const TEXT_PRESETS: Record<TextPreset, { name: string; text: string; sizeRatio: number; weight: number; uppercase: boolean }> = {
  headline: { name: 'Nagłówek', text: 'Twój nagłówek', sizeRatio: 0.085, weight: 900, uppercase: true },
  subtitle: { name: 'Podtytuł', text: 'Podtytuł kreacji', sizeRatio: 0.045, weight: 600, uppercase: false },
  body: { name: 'Akapit', text: 'Miejsce na opis oferty, warunki promocji albo dane kontaktowe.', sizeRatio: 0.028, weight: 400, uppercase: false },
  badge: { name: 'Plakietka', text: 'Sprawdź ofertę', sizeRatio: 0.032, weight: 700, uppercase: true },
};

export const createTextElement = (
  preset: TextPreset,
  sceneWidth: number,
  sceneHeight: number,
  /** Krój z ustawień generatora; bez niego wracamy do domyślnego. */
  fontFamily: string = DEFAULT_FONT,
): TextElement => {
  const config = TEXT_PRESETS[preset];
  const shortEdge = Math.min(sceneWidth, sceneHeight);
  const fontSize = Math.max(12, Math.round(shortEdge * config.sizeRatio));
  const width = Math.round(sceneWidth * (preset === 'badge' ? 0.45 : 0.8));
  const isBadge = preset === 'badge';

  return {
    ...base(config.name),
    type: 'text',
    text: config.text,
    x: Math.round((sceneWidth - width) / 2),
    y: Math.round(sceneHeight * 0.4),
    width,
    height: fontSize * 1.3,
    fontFamily,
    fontSize,
    fontWeight: config.weight,
    fontStyle: 'normal',
    color: isBadge ? '#0f3333' : '#ffffff',
    align: 'center',
    lineHeight: 1.2,
    letterSpacing: 0,
    uppercase: config.uppercase,
    background: isBadge ? '#c18344' : '',
    backgroundRadius: isBadge ? 999 : 0,
    padding: isBadge ? Math.round(fontSize * 0.6) : 0,
    stroke: '',
    strokeWidth: 0,
    autoFit: false,
  };
};

const SHAPE_NAMES: Record<ShapeKind, string> = {
  rect: 'Prostokąt',
  ellipse: 'Elipsa',
  triangle: 'Trójkąt',
  star: 'Gwiazda',
  line: 'Linia',
};

export const createShapeElement = (
  shape: ShapeKind,
  sceneWidth: number,
  sceneHeight: number,
): ShapeElement => {
  const placement = shape === 'line'
    ? { ...centerPlacement(sceneWidth, sceneHeight, 0.6, 0.02), height: Math.max(4, Math.round(sceneHeight * 0.008)) }
    : centerPlacement(sceneWidth, sceneHeight, 0.35, 0.35);

  return {
    ...base(SHAPE_NAMES[shape]),
    type: 'shape',
    shape,
    ...placement,
    fill: shape === 'line' ? '#ffffff' : '#c18344',
    stroke: '',
    strokeWidth: 0,
    radius: shape === 'rect' ? Math.round(Math.min(placement.width, placement.height) * 0.08) : 0,
  };
};

/**
 * Wstawia zdjęcie w rozmiarze zachowującym jego proporcje, przycięte do 70%
 * krótszego boku planszy, żeby duże pliki nie wychodziły poza kadr.
 */
export const createImageElement = (
  src: string,
  naturalWidth: number,
  naturalHeight: number,
  sceneWidth: number,
  sceneHeight: number,
): ImageElement => {
  const maxWidth = sceneWidth * 0.7;
  const maxHeight = sceneHeight * 0.7;
  const ratio = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight, 1);
  const width = Math.round(naturalWidth * ratio);
  const height = Math.round(naturalHeight * ratio);

  return {
    ...base('Zdjęcie'),
    type: 'image',
    src,
    x: Math.round((sceneWidth - width) / 2),
    y: Math.round((sceneHeight - height) / 2),
    width,
    height,
    radius: 0,
    fit: 'cover',
    circle: false,
    focusX: 0.5,
    focusY: 0.5,
    blur: 0,
    brightness: 0,
    contrast: 0,
    grayscale: false,
  };
};

/** Rodzina czcionek gotowa do podania Konvie i CSS. */
export const elementFontFamily = (element: TextElement): string => cssFontFamily(element.fontFamily);

export const isTextElement = (element: AdElement): element is TextElement => element.type === 'text';
export const isShapeElement = (element: AdElement): element is ShapeElement => element.type === 'shape';
export const isImageElement = (element: AdElement): element is ImageElement => element.type === 'image';
